<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header: project picker + create sprint -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">Sprints &amp; Backlog</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Plan sprints, then pull backlog items in.</p>
      </div>
      <div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="sm:flex-1">
          <v-autocomplete
            density="compact"
            label="Project"
            variant="solo-filled"
            flat
            hide-details
            :items="projectOptions"
            item-title="title"
            item-value="id"
            v-model="selectedProject"
            @update:modelValue="onProjectChange"
          ></v-autocomplete>
        </div>
        <div class="flex items-center gap-2">
          <AddProject @created="onProjectCreated" @toggle-message="notify" />
          <button
            type="button"
            :disabled="!selectedProject || isAllProjects"
            :title="isAllProjects ? 'Pick a single project to create a sprint' : ''"
            @click="openCreate"
            class="inline-flex items-center gap-1.5 rounded-xl bg-amber-400 px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition active:scale-95 disabled:opacity-50"
          ><v-icon size="18">mdi-plus</v-icon>New Sprint</button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3" :class="isAllProjects ? '' : 'lg:grid-cols-12'">
      <!-- Sprints -->
      <section class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900" :class="isAllProjects ? '' : 'lg:col-span-7'">
        <div class="mb-2 flex items-center gap-2">
          <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Sprints</h2>
          <span v-if="isAllProjects" class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">All projects</span>
        </div>
        <EmptyState v-if="sprints.length === 0" icon="mdi-run-fast" title="No sprints yet for this project." dense />
        <div v-else class="space-y-2">
          <div v-for="sprint in sprints" :key="sprint.id" class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ sprint.name }}</h3>
                  <span class="rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
                    :class="{
                      active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
                      completed: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300',
                      planned: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
                    }[sprint.status] || 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300'">{{ sprint.status }}</span>
                  <span v-if="isAllProjects && sprint.project" class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                    <v-icon size="11">mdi-folder-outline</v-icon>{{ sprint.project.title }}
                  </span>
                </div>
                <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{{ sprint.goal || 'No goal set' }}</p>
                <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <span class="inline-flex items-center gap-1"><v-icon size="14">mdi-calendar</v-icon>{{ sprint.start_date || '—' }} → {{ sprint.end_date || '—' }}</span>
                  <span class="inline-flex items-center gap-1"><v-icon size="14">mdi-ticket</v-icon>{{ sprint.ticket_count }} tickets</span>
                </div>
              </div>
              <div class="flex shrink-0 flex-col items-end gap-1">
                <button v-if="sprint.status === 'planned'" type="button" @click="startSprint(sprint)"
                  class="rounded-lg bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 transition active:scale-95 dark:bg-emerald-500/15 dark:text-emerald-300">Start</button>
                <button v-if="sprint.status === 'active'" type="button" @click="completeSprint(sprint)"
                  class="rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 transition active:scale-95 dark:bg-amber-500/15 dark:text-amber-300">Complete</button>
                <button v-if="sprint.status === 'completed'" type="button" @click="reopenSprint(sprint)"
                  class="rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 transition active:scale-95 dark:bg-blue-500/15 dark:text-blue-300">Reopen</button>
                <button type="button" aria-label="Edit sprint" @click="openEdit(sprint)"
                  class="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="18">mdi-pencil-outline</v-icon></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Backlog (per-project only) -->
      <section v-if="!isAllProjects" class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-5">
        <div class="mb-2 flex items-center gap-2">
          <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Backlog</h2>
          <span class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ backlog.length }}</span>
        </div>
        <EmptyState v-if="backlog.length === 0" icon="mdi-inbox-outline" title="Backlog is empty." dense />
        <div v-else class="space-y-2">
          <div v-for="ticket in backlog" :key="ticket.id" class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
            <p class="inline-flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-300"><v-icon size="13">mdi-ticket</v-icon>{{ ticket.ticket_id }} — {{ ticket.name }}</p>
            <div class="mt-2 flex items-center gap-2">
              <div class="flex-1">
                <v-select density="compact" variant="solo-filled" flat hide-details label="Move to sprint" :items="assignableSprints" item-title="name" item-value="id" v-model="assignTarget[ticket.id]"></v-select>
              </div>
              <button type="button" :disabled="!assignTarget[ticket.id]" @click="assignToSprint(ticket)"
                class="inline-flex shrink-0 items-center rounded-lg bg-amber-400 px-3 py-2 text-xs font-semibold text-gray-900 transition active:scale-95 disabled:opacity-50">Add</button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Create / Edit dialog -->
    <v-dialog v-model="dialog" max-width="560">
      <v-card :title="editing ? 'Edit Sprint' : 'Create Sprint'" prepend-icon="mdi-run-fast">
        <v-card-text>
          <v-text-field density="compact" variant="outlined" label="Name*" v-model="form.name"></v-text-field>
          <v-textarea density="compact" variant="outlined" label="Goal" rows="2" v-model="form.goal"></v-textarea>
          <v-row dense>
            <v-col cols="6">
              <v-text-field density="compact" variant="outlined" type="date" label="Start date" v-model="form.start_date"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field density="compact" variant="outlined" type="date" label="End date" :min="endDateMin" v-model="form.end_date"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Close" variant="plain" @click="dialog = false"></v-btn>
          <v-btn color="primary" text="Save" variant="flat" @click="saveSprint"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import Snackbar from './Snackbar.vue';
import AddProject from './AddProject.vue';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import EmptyState from '@/components/ui/EmptyState.vue';

export default {
  name: 'SprintPage',
  components: { Snackbar, AddProject, EmptyState },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant: constant,
      projects: [],
      sprints: [],
      backlog: [],
      selectedProject: null,
      assignTarget: {},
      dialog: false,
      editing: false,
      form: { id: null, name: '', goal: '', start_date: null, end_date: null },
      isshow: false,
      message: '',
    };
  },
  computed: {
    // Whether the "All Projects" aggregate view is selected.
    isAllProjects() {
      return this.selectedProject === 'ALL';
    },
    // Project dropdown items, with an "All Projects" aggregate entry on top.
    projectOptions() {
      if (!this.projects.length) return [];
      return [{ id: 'ALL', title: 'All Projects' }, ...this.projects];
    },
    // Tickets can only be pulled into sprints that aren't completed.
    assignableSprints() {
      return this.sprints.filter(s => s.status !== 'completed');
    },
    // The end date must be strictly after the start date.
    endDateMin() {
      if (!this.form.start_date) return null;
      const d = new Date(this.form.start_date);
      d.setDate(d.getDate() + 1);
      const off = d.getTimezoneOffset();
      return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
    },
  },
  methods: {
    // Sprint lifecycle statuses (active / completed / planned) — distinct from
    // ticket statuses, so this keeps its own color map rather than the mixin's.
    statusColor(status) {
      return status === 'active' ? 'green' : status === 'completed' ? 'grey' : 'blue';
    },
    notify(msg) {
      this.isshow = true;
      this.message = msg;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    getProjects() {
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => {
          this.projects = response.data.data || [];
          if (this.projects.length && !this.selectedProject) {
            this.selectedProject = this.projects[0].id;
            this.onProjectChange();
          }
        })
        .catch(error => console.error('Error fetching projects:', error));
    },
    onProjectChange() {
      if (!this.selectedProject) return;
      if (this.isAllProjects) {
        // Aggregate every sprint across all accessible projects; backlog is
        // project-specific so it's not shown in this mode.
        this.backlog = [];
        this.getAllSprints();
        return;
      }
      this.getSprints();
      this.getBacklog();
    },
    // Refresh the sprint list using whichever mode is active.
    refreshSprints() {
      if (this.isAllProjects) {
        this.getAllSprints();
      } else {
        this.getSprints();
      }
    },
    getAllSprints() {
      axios.get(utils._api(this.constant.list_all_sprints), this.authHeaders())
        .then((response) => { this.sprints = response.data.data || []; })
        .catch(error => console.error('Error fetching all sprints:', error));
    },
    onProjectCreated(project) {
      // Refresh the list, then select the newly created project.
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => {
          this.projects = response.data.data || [];
          if (project && project.id) {
            this.selectedProject = project.id;
            this.onProjectChange();
          }
        })
        .catch(error => console.error('Error fetching projects:', error));
    },
    getSprints() {
      axios.post(utils._api(this.constant.list_sprints),
        { project_id: this.selectedProject }, this.authHeaders())
        .then((response) => { this.sprints = response.data.data || []; })
        .catch(error => console.error('Error fetching sprints:', error));
    },
    getBacklog() {
      axios.post(utils._api(this.constant.sprint_backlog),
        { project_id: this.selectedProject }, this.authHeaders())
        .then((response) => { this.backlog = response.data.data || []; })
        .catch(error => console.error('Error fetching backlog:', error));
    },
    openCreate() {
      this.editing = false;
      this.form = { id: null, name: '', goal: '', start_date: null, end_date: null };
      this.dialog = true;
    },
    openEdit(sprint) {
      this.editing = true;
      this.form = {
        id: sprint.id,
        name: sprint.name,
        goal: sprint.goal,
        start_date: sprint.start_date,
        end_date: sprint.end_date,
      };
      this.dialog = true;
    },
    saveSprint() {
      // End date must be strictly after the start date.
      if (this.form.start_date && this.form.end_date && this.form.end_date <= this.form.start_date) {
        this.notify('End date must be after the start date');
        return;
      }
      const url = this.editing ? this.constant.update_sprint : this.constant.create_sprint;
      const payload = this.editing
        ? { ...this.form }
        : { ...this.form, project_id: this.selectedProject };
      axios.post(utils._api(url), payload, this.authHeaders())
        .then((response) => {
          this.dialog = false;
          this.notify(response.data.message);
          this.refreshSprints();
        })
        .catch(error => {
          console.error('Error saving sprint:', error);
          const msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Failed to save sprint';
          this.notify(msg);
        });
    },
    startSprint(sprint) {
      axios.post(utils._api(this.constant.start_sprint), { id: sprint.id }, this.authHeaders())
        .then((response) => { this.notify(response.data.message); this.refreshSprints(); })
        .catch(error => console.error('Error starting sprint:', error));
    },
    completeSprint(sprint) {
      axios.post(utils._api(this.constant.complete_sprint), { id: sprint.id }, this.authHeaders())
        .then((response) => {
          this.notify(response.data.message);
          this.refreshSprints();
          if (!this.isAllProjects) this.getBacklog();
        })
        .catch(error => console.error('Error completing sprint:', error));
    },
    reopenSprint(sprint) {
      axios.post(utils._api(this.constant.reopen_sprint), { id: sprint.id }, this.authHeaders())
        .then((response) => { this.notify(response.data.message); this.refreshSprints(); })
        .catch(error => console.error('Error reopening sprint:', error));
    },
    assignToSprint(ticket) {
      const sprintId = this.assignTarget[ticket.id];
      axios.post(utils._api(this.constant.assign_ticket),
        { ticket_db_id: ticket.id, sprint_id: sprintId }, this.authHeaders())
        .then((response) => {
          this.notify(response.data.message);
          this.getBacklog();
          this.getSprints();
        })
        .catch(error => console.error('Error assigning ticket:', error));
    },
  },
  mounted() {
    this.getProjects();
  }
};
</script>
